import React, { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';

const StoreLocator = ({ isAdmin }) => {
  const [locations, setLocations] = useState([]);
  const [newAddress, setNewAddress] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [map, setMap] = useState(null);

  const db = getFirestore();

  const fetchLocations = async () => {
    try {
      setIsLoading(true);
      const locationsCollection = collection(db, 'Locations');
      const locationSnapshot = await getDocs(locationsCollection);
      
      const locationList = locationSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          lat: parseFloat(data.lat),
          lng: parseFloat(data.lng)
        };
      }).filter(loc => !isNaN(loc.lat) && !isNaN(loc.lng)); // Filter out invalid coordinates

      console.log('Valid locations:', locationList);
      setLocations(locationList);

      // Center map on first valid location
      if (locationList.length > 0) {
        const newCenter = {
          lat: locationList[0].lat,
          lng: locationList[0].lng
        };
        setMapCenter(newCenter);
        if (map) {
          map.panTo(newCenter);
        }
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to load locations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const addLocation = async () => {
    if (!newAddress) return;
    
    try {
      const geocoder = new window.google.maps.Geocoder();
      
      const results = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: newAddress }, (results, status) => {
          if (status === 'OK') resolve(results);
          else reject(new Error(status));
        });
      });

      const location = results[0].geometry.location;
      const newLocation = {
        address: newAddress,
        lat: location.lat(),
        lng: location.lng(),
        createdAt: new Date().toISOString()
      };

      console.log('Adding location with coordinates:', newLocation);

      await addDoc(collection(db, 'Locations'), newLocation);
      setNewAddress('');
      fetchLocations();

      // Center map on new location
      const newCenter = { lat: newLocation.lat, lng: newLocation.lng };
      setMapCenter(newCenter);
      if (map) {
        map.panTo(newCenter);
        map.setZoom(15); // Zoom in when adding new location
      }
    } catch (err) {
      console.error('Error adding location:', err);
      setError(`Failed to add location: ${err.message}`);
    }
  };

  const containerStyle = {
    width: '100%',
    height: '400px'
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4">
          {error}
        </div>
      )}

      <div style={{ height: '400px' }} className="rounded-lg overflow-hidden">
        <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={12}
            onLoad={(mapInstance) => {
              console.log('Map loaded');
              setMap(mapInstance);
            }}
          >
            {locations.map((location) => (
              <Marker
                key={location.id}
                position={{
                  lat: location.lat,
                  lng: location.lng
                }}
                icon={{
                  url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
                  scaledSize: new window.google.maps.Size(40, 40),
                }}
                onClick={() => {
                  setSelectedLocation(location);
                  if (map) {
                    map.panTo({ lat: location.lat, lng: location.lng });
                  }
                }}
              />
            ))}

            {selectedLocation && (
              <InfoWindow
                position={{
                  lat: selectedLocation.lat,
                  lng: selectedLocation.lng
                }}
                onCloseClick={() => setSelectedLocation(null)}
              >
                <div>
                  <h3 className="font-bold">{selectedLocation.address}</h3>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </LoadScript>
      </div>

      {isAdmin && (
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex gap-2">
            <input
              type="text"
              value={newAddress}
              onChange={(e) => setNewAddress(e.target.value)}
              placeholder="Enter address"
              className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button 
              onClick={addLocation}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Add Location
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="font-bold text-lg mb-4">Store Locations ({locations.length})</h3>
        {locations.length === 0 ? (
          <p className="text-gray-500 text-center">No locations found</p>
        ) : (
          <div className="space-y-2">
            {locations.map((location) => (
              <div 
                key={location.id} 
                className="p-3 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => {
                  setSelectedLocation(location);
                  if (map) {
                    map.panTo({ lat: location.lat, lng: location.lng });
                    map.setZoom(15);
                  }
                }}
              >
                <div className="flex justify-between items-center">
                  <span>{location.address}</span>
                  {isAdmin && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this location?')) {
                          deleteDoc(doc(db, 'Locations', location.id))
                            .then(fetchLocations)
                            .catch(err => setError(`Failed to delete location: ${err.message}`));
                        }
                      }}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                    >
                      Delete
                    </button>
                  )}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Coordinates: {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreLocator;